// @flow
/* eslint-disable react/jsx-pascal-case, camelcase */

import React from 'react';
import { pick, isEqual, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import type { Store } from 'redux';
import { connect } from 'react-redux';
import { SourceType, isSlingSourceType } from 'Common/models/franchise';
import { Box } from 'Common/components';
import { getRadishSession } from 'Common/auth/actions';
import { setPlayerStream, setDVRStream, setLiveStream } from 'Common/player/actions';
import { resetStore } from 'Common/cast/actions';
import { mapAustinErrorToErrorMessage } from 'Common/cast/CastUtils';
import { AustinError } from 'Common/cast/types';
import { ChromeCastReceiver, ReceiverStateMachine } from 'Browser/cast/receiver';
import { DebugInfo, CommonUi } from 'Browser/cast/receiver/receiverUi';

import type { PlayerState, Metadata, AustinEvent } from 'Common/cast/types';
import type { State as AppState, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import type { ActiveReceiver } from 'Common/app/types';
import type { ReceiverUiState } from 'Browser/cast/receiver/ReceiverUiStates';
import type { SourceType as SourceTypeT } from 'Common/player/types';
import { logBillingActiveUserEvent } from 'Common/analytics/vmanalytics/actions';

import { ReceiverUiStates } from './ReceiverUiStates';
import { CastReceiverStyles as styles } from './styles';

const CAST_MEDIA_PLAYER_TAG = 'cast-media-player';
/* NOTE: Chromecast uses media element (video tag) with the following id. */
const CAST_MEDIA_ELEMENT_ID = 'castMediaElement';
const LOGGING_ENABLED = false;

const INITIAL_STATE = {
  playerState: [],
  austinState: [],
  metadata: undefined,
  austinErrorMessage: undefined,
  receiverUiState: 'idle',
  isPlayerUIHidden: true,
};

type State = {|
  playerState: Array<PlayerState>,
  austinState: Array<AustinEvent>,
  receiverUiState: ReceiverUiState,
  metadata: ?Metadata,
  austinErrorMessage: ?string,
  isPlayerUIHidden: boolean,
|};

type Props = {|
  isSignedIn: boolean,
  resetStore: () => void,
  playerKey: string,
  sourceType: SourceTypeT,
  activeReceiver: ActiveReceiver,
  playbackCouldNotBeStarted: boolean,
  uuid: string,

  getRadishSession: typeof getRadishSession,
  setPlayerStream: typeof setPlayerStream,
  setDVRStream: typeof setDVRStream,
  setLiveStream: typeof setLiveStream,
  logBillingActiveUserEvent: typeof logBillingActiveUserEvent,
|};

class CastReceiver extends React.Component<Props, State> {
  receiver: ChromeCastReceiver;
  stateMachine: ReceiverStateMachine;
  hideControlsBarTimeoutID: TimeoutID;
  hidePlayerUITimeoutID: TimeoutID;
  store: Store<AppState, BaseAction | ThunkAction, Dispatch>;

  static contextTypes = { store: PropTypes.object };

  static getCastVideoElement = () => {
    const { shadowRoot } = document.getElementsByTagName(CAST_MEDIA_PLAYER_TAG)[0];
    return (shadowRoot: any).getElementById(CAST_MEDIA_ELEMENT_ID);
  };

  static log = (message: string, ...data: Array<any>) => {
    if (!LOGGING_ENABLED || !process.env.IN_DEBUG) return;
    console.log(message, ...data);
  };

  static resetMediaElement = async () => {
    const videoElement = CastReceiver.getCastVideoElement();
    videoElement.setAttribute('src', '');
    videoElement.load();

    try {
      await videoElement.setMediaKeys(null);
    } catch {
      console.warn('[CAST][MEDIA_ELEMENT] Fail to reset media keys.');
    }
  };

  static observeStore(
    store: Store<AppState, BaseAction | ThunkAction, Dispatch>,
    paths: Array<string>,
    onChange: Object => void,
  ) {
    let currentState;

    function handleChange() {
      const nextState = pick(store.getState(), paths);
      if (!isEqual(nextState, currentState)) {
        currentState = nextState;
        onChange(nextState);
      }
    }

    const unsubscribe = store.subscribe(handleChange);
    // handleChange();
    return unsubscribe;
  }

  constructor(props, context) {
    super(props);
    this.store = context.store;
  }

  state = { ...INITIAL_STATE };

  componentDidMount() {
    this.removeSplash();
    setTimeout(this.hideOutdatedPlayerEvents, 1000);
    setTimeout(this.hideOutDatedAustinEvents, 3000);
    this.startReceiver();
    this.stateMachine = new ReceiverStateMachine(this.onReceiverUiStateChange);
  }

  async componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      isSignedIn: prevIsSignedIn,
      playbackCouldNotBeStarted: prevPlaybackCouldNotBeStarted,
      sourceType: prevSourceType,
      activeReceiver: { receiverId: prevReceiverId } = {},
      uuid: prevUuid,
    } = prevProps;

    const {
      isSignedIn,
      sourceType,
      activeReceiver: { receiverId } = {},
      uuid,
      logBillingActiveUserEvent,
    } = this.props;

    if (isSignedIn !== prevIsSignedIn && this.receiver) {
      this.receiver.setIsSignedIn(isSignedIn);
      logBillingActiveUserEvent();
    }

    const { playbackCouldNotBeStarted } = this.props;
    if (prevPlaybackCouldNotBeStarted === false && playbackCouldNotBeStarted === true) {
      this.passEventToStateMachine('ERROR');
    }

    const { receiverUiState: prevReceiverUiState } = prevState;
    const { receiverUiState } = this.state;

    if (prevReceiverUiState !== receiverUiState) {
      if (ReceiverUiStates.isIdle(receiverUiState)) await this.onIdleTransition();
      if (ReceiverUiStates.isPlaying(receiverUiState)) this.scheduleControlsBarHiding();
      if (ReceiverUiStates.isPaused(receiverUiState)) this.showControlsBar();
      if (
        ReceiverUiStates.isPlaying(receiverUiState) ||
        ReceiverUiStates.isBitmovinPlaying(receiverUiState)
      ) {
        this.restorePlayerUI();
      }

      if (
        ReceiverUiStates.isPaused(receiverUiState) ||
        ReceiverUiStates.isBitmovinPaused(receiverUiState)
      ) {
        this.schedulePlayerUIHiding();
      }
    }

    if (prevReceiverId !== receiverId && receiverId) {
      this.receiver.onReceiverChange(receiverId);
    }

    if (prevUuid !== uuid && uuid) {
      this.receiver.onUuidChange(uuid);
    }
  }

  async onIdleTransition() {
    this.resetState();
    await CastReceiver.resetMediaElement();
    this.notifySendersAboutIdleState();
  }

  removeSplash() {
    /* Remove splash preload once react gets loaded */
    const preLoadSplash = document.getElementById('splash_preload');
    if (preLoadSplash) preLoadSplash.remove();
  }

  onReceiverUiStateChange = (receiverUiState: ReceiverUiState) =>
    this.setState({ receiverUiState });

  hideOutdatedPlayerEvents = () => {
    const { playerState } = this.state;

    if (playerState.length > 1) {
      const [, ...restOfEvents] = playerState;
      this.setState({ playerState: restOfEvents });
    }

    setTimeout(this.hideOutdatedPlayerEvents, playerState.length > 5 ? 250 : 500);
  };

  hideOutDatedAustinEvents = () => {
    const { austinState } = this.state;

    if (austinState.length > 1) {
      const [, ...restOfEvents] = austinState;
      this.setState({ austinState: restOfEvents });
    }

    setTimeout(this.hideOutDatedAustinEvents, 3000);
  };

  setMetadata = (metadata: Metadata) => this.setState({ metadata });

  startReceiver = () => {
    const {
      resetStore,
      getRadishSession,
      setDVRStream,
      setLiveStream,
      playerKey,
      setPlayerStream,
    } = this.props;

    this.receiver = new ChromeCastReceiver(
      this.store,
      resetStore,
      playerKey,
      this.onPlaybackFinished,
      this.onAustinStateChanged,
      this.passEventToStateMachine,
      this.onPlayerStateChanged,
      this.setMetadata,
      getRadishSession,
      setPlayerStream,
      setDVRStream,
      setLiveStream,
    );

    this.receiver.start();

    if (process.env.IN_DEBUG) {
      window.receiver = this.receiver;
      window.setUiState = this.onReceiverUiStateChange;
    }
  };

  onPlayerStateChanged = (event: { type: string, isBuffering?: boolean }) => {
    const { type } = event;

    if (type === 'BUFFERING' && event.isBuffering === true) {
      this.setState(state => ({ playerState: [...state.playerState, type] }));
    } else if (
      type === 'IDLE' ||
      type === 'PLAYING' ||
      type === 'PAUSE' ||
      type === 'SEEKING' ||
      type === 'SEEKED' ||
      type === 'PLAYER_LOADING' ||
      type === 'REQUEST_LOAD' ||
      type === 'PLAYER_LOAD_COMPLETE' ||
      type === 'REQUEST_PAUSE' ||
      type === 'REQUEST_PLAY' ||
      type === 'REQUEST_SEEK' ||
      type === 'ERROR'
    ) {
      if (type !== 'ERROR') {
        this.setState(state => ({ playerState: [...state.playerState, type] }));
      } else {
        this.setState(state => ({
          // $FlowFixMe
          playerState: [...state.playerState, `${type} ${event.detailedErrorCode}`],
        }));
      }
    }

    this.passEventToStateMachine(type);
  };

  passEventToStateMachine = (event: string) => {
    const { sourceType } = this.props;
    const isAustinEvent = isSlingSourceType(sourceType);

    if (event === 'PLAYER_LOAD_COMPLETE' && (isAustinEvent || !sourceType)) {
      return;
    }

    if (event === 'pause') {
      event = isAustinEvent ? 'austin_pause' : 'bitmovin_pause';
    } else if (event === 'PLAYING') {
      event = isAustinEvent ? 'austin_play' : 'bitmovin_play';
    }

    this.stateMachine.handleEvent(event, isSlingSourceType(sourceType));
  };

  onAustinStateChanged = (event: AustinEvent) => {
    CastReceiver.log('[CAST][AUSTIN] State changed ', event);
    const { austinState } = this.state;

    this.setState({ austinState: [...austinState, event] });

    if (event.type === 'onStatusChange' || event.type === 'onInfo') {
      const state = event.type === 'onStatusChange' ? event.payload : event.payload.reason;
      this.passEventToStateMachine(state);

      if (event.payload.type === 'discontinuity' && event.payload.reason === 'channel_change') {
        /* Triggers sending new media info to senders.
           Such situation occurs when Austin changes channel or DVR without LoadRequest.
           Added timeout because Austin frequently gives previous media information if asked right at event change */
        setTimeout(() => this.receiver.syncMediaInformation(), 2000);
      }
    } else if (
      event.type === 'onStallStart' &&
      (event.payload.type === 'stallstarted' ||
        event.payload.type === 'seeked' ||
        event.payload.type === 'seek')
    ) {
      this.passEventToStateMachine(event.payload.type);
    } else if (event.type === 'onPause') {
      this.passEventToStateMachine('austin_pause');
    } else if (event.type === 'configure') {
      /* Treat 'configure' action as REQUEST_LOAD because 'configure' action triggers content re-loading */
      this.passEventToStateMachine('REQUEST_LOAD');
    } else if (event.type === 'onError') {
      const error =
        event.payload instanceof AustinError ? event.payload.type : event.payload.reason;

      const austinErrorMessage = mapAustinErrorToErrorMessage(error);

      this.setState({ austinState: [event], austinErrorMessage });
      this.passEventToStateMachine('onError');
    }
  };

  resetState = () => {
    CastReceiver.log('[CAST] Transiting to "IDLE" state.');
    this.setState({ ...INITIAL_STATE });
  };

  onPlaybackFinished = () => {
    const { sourceType } = this.props;

    /* LL never ends */
    if (sourceType !== SourceType.LIVE_LINEAR) {
      this.passEventToStateMachine('playbackFinished');
      this.passEventToStateMachine('idle');
    }
  };

  handleBitmovinError = () => this.passEventToStateMachine('bitmovinError');

  renderUiForCurrentState(state: ReceiverUiState) {
    const { sourceType } = this.props;
    const { metadata, isPlayerUIHidden } = this.state;
    const isSling = isSlingSourceType(sourceType);

    switch (state) {
      case 'idle':
        return <CommonUi.Idle />;
      case 'loading_asset':
        return isSling ? <CommonUi.Loading /> : null;
      case 'Austin_connecting': {
        const { activeReceiver } = this.props;
        return <CommonUi.Connecting to={activeReceiver.nickname} metadata={metadata} />;
      }
      case 'Austin_starting_streaming':
        return <CommonUi.StartingStreaming metadata={metadata} />;
      case 'Austin_stopping_streaming':
        return <CommonUi.StoppingStreaming />;
      case 'Austin_error': {
        const { austinErrorMessage } = this.state;
        return typeof austinErrorMessage === 'string' ? (
          <CommonUi.Error message={austinErrorMessage} />
        ) : (
          <CommonUi.Error />
        );
      }
      case 'Austin_changing_channel':
        return <CommonUi.ChangingChannel />;
      case 'Austin_seeking':
        return <CommonUi.Seeking />;
      case 'Austin_disconnecting':
        return <CommonUi.Disconnecting />;
      case 'Bitmovin_error':
        return <CommonUi.Error />;
      default:
        /* If following line doesn't throw Flow errors then all possible ui states have been handled */
        // (state: empty); // eslint-disable-line no-unused-expressions
        return undefined;
    }
  }

  notifySendersAboutIdleState() {
    const msg = { type: 'RECEIVER_IS_IDLE' };
    const onSend = (senderId: string) =>
      CastReceiver.log(`[CAST] Notifying "${senderId}" about receiver entering IDLE state.`);

    ChromeCastReceiver.sendMessageToAllSenders(msg, onSend);
  }

  scheduleControlsBarHiding() {
    clearTimeout(this.hideControlsBarTimeoutID);

    this.hideControlsBarTimeoutID = setTimeout(() => {
      const { receiverUiState } = this.state;
      if (ReceiverUiStates.isPlaying(receiverUiState)) this.hideControlsBar();
    }, 1000);
  }

  schedulePlayerUIHiding() {
    clearTimeout(this.hidePlayerUITimeoutID);

    this.hidePlayerUITimeoutID = setTimeout(() => {
      const { receiverUiState } = this.state;
      if (
        ReceiverUiStates.isPaused(receiverUiState) ||
        ReceiverUiStates.isBitmovinPaused(receiverUiState)
      )
        this.hidePlayerUI();
    }, 5000);
  }

  hidePlayerUI = () => {
    this.hidePlaybackButton();
    this.hideControlsBar();
    this.setState({ isPlayerUIHidden: true });
  };

  restorePlayerUI = () => {
    this.showPlaybackButton();
    this.showControlsBar();
    this.setState({ isPlayerUIHidden: false });
  };

  hidePlaybackButton = () => {
    const playbackButton = this._getPlaybackButton();
    if (playbackButton) playbackButton.hidden = true;
  };

  showPlaybackButton = () => {
    const playbackButton = this._getPlaybackButton();
    if (playbackButton) playbackButton.hidden = false;
  };

  _getPlaybackButton = () => document.querySelector('.bmpui-ui-hugeplaybacktogglebutton'); // Play/Pause

  hideControlsBar = () => {
    const playerControlsBar = this._getControlsBar();
    if (playerControlsBar) playerControlsBar.hidden = true;
  };

  showControlsBar = () => {
    const playerControlsBar = this._getControlsBar();
    if (playerControlsBar) playerControlsBar.hidden = false;
  };

  _getControlsBar = () => document.querySelector('.bmpui-ui-controlbar');

  render() {
    const { playerState, austinState, receiverUiState } = this.state;

    return (
      <>
        {process.env.IN_DEBUG && (
          <DebugInfo
            playerState={playerState}
            austinState={austinState}
            receiverUiState={receiverUiState}
          />
        )}

        {this.renderUiForCurrentState(receiverUiState)}

        <Box style={styles.playerWrapper(ReceiverUiStates.isIdle(receiverUiState))}>
          <cast-media-player />
        </Box>
      </>
    );
  }
}

export default connect(
  (state: AppState) => ({
    isSignedIn: state.auth.signInType === 'online',
    playerKey: state.config.bitmovin.player_key,
    sourceType: state.player.sourceType,
    activeReceiver: state.app.activeReceiver,
    playbackCouldNotBeStarted: state.player.playbackCouldNotBeStarted ?? false,
    uuid: state.auth.viewer?.uuid,
  }),
  {
    resetStore,
    getRadishSession,
    setPlayerStream,
    setDVRStream,
    setLiveStream,
    logBillingActiveUserEvent,
  },
)(CastReceiver);
